import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { pseoAPI, articlesAPI } from '../utils/api';
import { SEO } from '../components/SEO';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ArticleCard } from '../components/ArticleCard';
import { FAQSection } from '../components/FAQSection';
import { Newsletter } from '../components/Newsletter';
import { useRegion } from '../context/RegionContext';
import { Gift, Calendar, ArrowRight, Percent, Clock, Tag, ShoppingBag } from 'lucide-react';

/**
 * Sale Event Detail Page
 * Shows details for a specific sale event (Black Friday, 11.11, etc.)
 */
export const SaleEventDetailPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentRegion } = useRegion();

  const buildUrl = (path) => {
    if (currentRegion === 'global') return path;
    return `/${currentRegion}${path}`;
  };

  useEffect(() => {
    const fetchEventData = async () => {
      setLoading(true);
      try {
        // Fetch event details from pSEO API
        const eventResponse = await pseoAPI.getPageData('event', slug);
        setEvent(eventResponse.data);
        
        // Fetch related articles
        const articlesResponse = await articlesAPI.getAll({ 
          tag: slug.replace(/-/g, ' '),
          is_published: true,
          limit: 12
        });
        setArticles(articlesResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch sale event:', error);
        // Create fallback event data
        setEvent({
          title: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          description: `Find the best deals and discounts for ${slug.replace(/-/g, ' ')}.`,
          meta_title: `${slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} - Best Deals & Offers`,
          meta_description: `Discover exclusive deals, promo codes, and discounts for ${slug.replace(/-/g, ' ')}.`
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchEventData();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading sale event...</p>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Sale Events', path: '/sale-events' }
  ];

  return (
    <>
      <SEO 
        title={event?.meta_title || event?.title || 'Sale Event'}
        description={event?.meta_description || event?.description}
        type="website"
      />

      <div className="min-h-screen bg-gray-50" data-testid="sale-event-detail-page">
        <Breadcrumbs items={breadcrumbItems} currentPage={event?.title || slug} />

        {/* Hero */}
        <div className="relative">
          {event?.banner_image && (
            <div className="absolute inset-0">
              <img 
                src={event.banner_image} 
                alt={event.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-red/90 to-red-700/80"></div>
            </div>
          )}
          <div className={`relative ${!event?.banner_image ? 'bg-gradient-to-r from-brand-red to-red-700' : ''} text-white py-16`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-brand-yellow text-gray-900 text-xs font-bold uppercase rounded-full">
                      Sale Event
                    </span>
                    {event?.start_date && (
                      <span className="flex items-center gap-1 text-white/80 text-sm">
                        <Calendar className="w-4 h-4" />
                        {event.start_date} {event.end_date && `- ${event.end_date}`}
                      </span>
                    )}
                  </div>
                  <h1 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
                    {event?.title || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </h1>
                  <p className="text-red-100 text-lg max-w-2xl">
                    {event?.short_description || event?.description?.slice(0, 200)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Description */}
          {event?.description && (
            <div className="bg-white rounded-2xl p-8 mb-12 border border-gray-100">
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>
          )}

          {/* Related Articles */}
          {articles.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Tag className="w-6 h-6 text-brand-red" />
                Related Deals & Guides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          )}

          {/* FAQs */}
          {event?.faqs && event.faqs.length > 0 && (
            <FAQSection faqs={event.faqs} title={`${event.title} FAQs`} />
          )}

          {/* Related Brands */}
          {event?.related_brands && event.related_brands.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-6 flex items-center gap-2">
                <ShoppingBag className="w-6 h-6 text-brand-green" />
                Participating Brands
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {event.related_brands.map((brandSlug) => (
                  <Link
                    key={brandSlug}
                    to={buildUrl(`/brands/${brandSlug}`)}
                    className="bg-white rounded-xl p-4 text-center hover:shadow-lg smooth-transition border border-gray-100 hover:border-brand-red"
                  >
                    <span className="font-semibold text-gray-900 capitalize">
                      {brandSlug.replace(/-/g, ' ')}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Newsletter */}
          <Newsletter />
        </div>
      </div>
    </>
  );
};

export default SaleEventDetailPage;
